import { buildMetricsPalette } from '@/shared/constants/metricsPalette';
import {
  featureStatusOptions,
  releaseStatusOptions,
  suiteRunSummaryStatusOptions,
  testRunSummaryStatusOptions,
} from '@/shared/constants/statusOptions';
import { uiTokens } from '@/theme/uiTokens';
import {
  formatDuration,
  formatFrequency,
  formatPercent,
  formatStatusLabel,
  getChipPalette,
  getStatusChipKey,
  type StatusPieSlice,
} from '@/ui';
import { alpha } from '@/ui/primitives';
import type { MetricThreshold } from '@/shared/utils/metricsThresholds';
import type {
  CiDisplayStatusKey,
  FeatureStatus,
  PipelineRunSummaryDto,
  PipelineSummaryDto,
  ReleaseStatus,
  SuiteRunStatus,
  TestRunStatus,
} from '@flowqa/shared';

export type PipelineStatusKey = CiDisplayStatusKey | 'none';
export type SuiteStatusKey = SuiteRunStatus | 'untested';
export type TestStatusKey = TestRunStatus | 'untested';

export const pipelineStatusDisplayOptions: Array<{ value: PipelineStatusKey; label: string }> = [
  { value: 'success', label: formatStatusLabel('success') },
  { value: 'warning', label: formatStatusLabel('warning') },
  { value: 'failed', label: formatStatusLabel('failed') },
  { value: 'running', label: formatStatusLabel('running') },
  { value: 'queued', label: formatStatusLabel('queued') },
  { value: 'canceled', label: formatStatusLabel('canceled') },
  { value: 'skipped', label: formatStatusLabel('skipped') },
  { value: 'neutral', label: formatStatusLabel('neutral') },
  { value: 'action_required', label: formatStatusLabel('action_required') },
  { value: 'timed_out', label: formatStatusLabel('timed_out') },
  { value: 'unknown', label: formatStatusLabel('unknown') },
  { value: 'none', label: 'No Jobs' },
];

const emptyPipelineDisplayCounts = (): Record<CiDisplayStatusKey, number> => ({
  queued: 0,
  running: 0,
  success: 0,
  warning: 0,
  failed: 0,
  canceled: 0,
  skipped: 0,
  neutral: 0,
  action_required: 0,
  timed_out: 0,
  unknown: 0,
});

export type ExecutionMetricDefinition<TSummary> = {
  title: string;
  format: (value: number | null | undefined) => string;
  getValue: (summary: TSummary | null | undefined) => number | null;
  getAccentValue?: (summary: TSummary | null | undefined) => number | null;
  threshold: MetricThreshold;
};

export const buildReleaseCounts = (
  releaseSummary:
    | { released?: number; overdue?: number; ready?: number; planned?: number; drafted?: number }
    | null
    | undefined,
): Partial<Record<ReleaseStatus, number>> => ({
  released: releaseSummary?.released ?? 0,
  overdue: releaseSummary?.overdue ?? 0,
  ready: releaseSummary?.ready ?? 0,
  planned: releaseSummary?.planned ?? 0,
  draft: releaseSummary?.drafted ?? 0,
});

export const buildFeatureCounts = (
  featureSummary:
    | { released?: number; ready?: number; planned?: number; drafted?: number }
    | null
    | undefined,
): Partial<Record<FeatureStatus, number>> => ({
  released: featureSummary?.released ?? 0,
  ready: featureSummary?.ready ?? 0,
  planned: featureSummary?.planned ?? 0,
  draft: featureSummary?.drafted ?? 0,
});

export const buildSuiteCounts = (
  suiteSummary:
    | { passed?: number; failed?: number; errored?: number; skipped?: number; untested?: number }
    | null
    | undefined,
): Partial<Record<SuiteStatusKey, number>> => ({
  passed: suiteSummary?.passed ?? 0,
  failed: suiteSummary?.failed ?? 0,
  errored: suiteSummary?.errored ?? 0,
  skipped: suiteSummary?.skipped ?? 0,
  untested: suiteSummary?.untested ?? 0,
});

export const buildTestCounts = (
  testSummary:
    | { passed?: number; failed?: number; errored?: number; skipped?: number; untested?: number }
    | null
    | undefined,
): Partial<Record<TestStatusKey, number>> => ({
  passed: testSummary?.passed ?? 0,
  failed: testSummary?.failed ?? 0,
  errored: testSummary?.errored ?? 0,
  skipped: testSummary?.skipped ?? 0,
  untested: testSummary?.untested ?? 0,
});

export const buildPipelineCounts = (
  pipelineSummary: PipelineSummaryDto | null | undefined,
): Partial<Record<PipelineStatusKey, number>> => ({
  ...(pipelineSummary?.displayStatusCounts ?? {
    ...emptyPipelineDisplayCounts(),
    success: pipelineSummary?.statusCounts.completed ?? 0,
    failed: pipelineSummary?.statusCounts.failed ?? 0,
    running: pipelineSummary?.statusCounts.running ?? 0,
    queued: pipelineSummary?.statusCounts.queued ?? 0,
  }),
  none: pipelineSummary?.noBuilds ?? 0,
});

export const buildPipelineCountsFromRunSummary = (
  pipelineSummary: PipelineRunSummaryDto | null | undefined,
): Partial<Record<PipelineStatusKey, number>> => ({
  ...(pipelineSummary?.displayStatusCounts ?? {
    ...emptyPipelineDisplayCounts(),
    success: pipelineSummary?.statusCounts.completed ?? 0,
    failed: pipelineSummary?.statusCounts.failed ?? 0,
    running: pipelineSummary?.statusCounts.running ?? 0,
    queued: pipelineSummary?.statusCounts.queued ?? 0,
  }),
  none: pipelineSummary?.noBuilds ?? 0,
});

export const buildReleaseSlices = (
  releaseCounts: Partial<Record<ReleaseStatus, number>>,
): Array<StatusPieSlice<ReleaseStatus>> =>
  releaseStatusOptions.map((option) => ({
    key: option.value,
    label: option.label,
    value: releaseCounts[option.value] ?? 0,
    color: alpha(getChipPalette(getStatusChipKey(option.value)).icon, 0.8),
  }));

export const buildFeatureSlices = (
  featureCounts: Partial<Record<FeatureStatus, number>>,
): Array<StatusPieSlice<FeatureStatus>> =>
  featureStatusOptions.map((option) => ({
    key: option.value,
    label: option.label,
    value: featureCounts[option.value] ?? 0,
    color: alpha(getChipPalette(getStatusChipKey(option.value)).icon, 0.8),
  }));

export const buildSuiteSlices = (
  suiteCounts: Partial<Record<SuiteStatusKey, number>>,
): Array<StatusPieSlice<SuiteStatusKey>> => {
  const metricsPalette = buildMetricsPalette();
  const options = [
    ...suiteRunSummaryStatusOptions,
    { value: 'untested' as const, label: 'Untested' },
  ];
  return options.map((option) => ({
    key: option.value,
    label: option.label,
    value: suiteCounts[option.value] ?? 0,
    color:
      option.value === 'untested'
        ? uiTokens.statusColors.neutral
        : option.value === 'passed'
          ? metricsPalette.pass
          : option.value === 'failed'
            ? metricsPalette.fail
            : option.value === 'errored'
              ? metricsPalette.error
              : metricsPalette.skip,
  }));
};

export const buildTestSlices = (
  testCounts: Partial<Record<TestStatusKey, number>>,
): Array<StatusPieSlice<TestStatusKey>> => {
  const metricsPalette = buildMetricsPalette();
  const options = [
    ...testRunSummaryStatusOptions,
    { value: 'untested' as const, label: 'Untested' },
  ];
  return options.map((option) => ({
    key: option.value,
    label: option.label,
    value: testCounts[option.value] ?? 0,
    color:
      option.value === 'untested'
        ? uiTokens.statusColors.neutral
        : option.value === 'passed'
          ? metricsPalette.pass
          : option.value === 'failed'
            ? metricsPalette.fail
            : option.value === 'errored'
              ? metricsPalette.error
              : metricsPalette.skip,
  }));
};

export const buildPipelineSlices = (
  pipelineCounts: Partial<Record<PipelineStatusKey, number>>,
): Array<StatusPieSlice<PipelineStatusKey>> => {
  return pipelineStatusDisplayOptions.map((option) => ({
    key: option.value,
    label: option.label,
    value: pipelineCounts[option.value] ?? 0,
    color: (() => {
      switch (option.value) {
        case 'success':
          return uiTokens.statusColors.green;
        case 'warning':
        case 'skipped':
        case 'action_required':
          return uiTokens.statusColors.orange;
        case 'failed':
        case 'canceled':
        case 'timed_out':
          return uiTokens.statusColors.red;
        case 'running':
          return uiTokens.statusColors.blue;
        case 'neutral':
        case 'unknown':
        case 'queued':
        case 'none':
          return uiTokens.statusColors.neutral;
      }
    })(),
  }));
};

export const formatPercentValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `${formatPercent(value)}%`;
};

export const formatFrequencyValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return formatFrequency(value);
};

export const formatDurationValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return formatDuration(value);
};

export const formatMttfValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return formatDuration(value * 1000);
};

export const formatAverageValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
